import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import featureOne from '../assets/FeaturesSection/1.png';
import featureTwo from '../assets/FeaturesSection/2.png';
import featureThree from '../assets/FeaturesSection/3.png';
import featureFour from '../assets/FeaturesSection/4.png';
import { useLanguage } from '../i18n/LanguageContext';

const FeaturesContainer = styled.section`
  padding: 120px 20px;
  background: radial-gradient(circle at top right, rgba(0, 168, 232, 0.08), transparent 50%),
    radial-gradient(circle at bottom left, rgba(12, 77, 162, 0.08), transparent 45%),
    #ffffff;

  @media (max-width: 768px) {
    padding: 90px 18px;
  }

  @media (max-width: 480px) {
    padding: 72px 14px;
  }
`;

const SectionHeadingArea = styled.header`
  max-width: 860px;
  margin: 0 auto 70px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    margin-bottom: 52px;
    gap: 14px;
  }
`;

const SectionTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(12, 77, 162, 0.12);
  color: var(--primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(2.2rem, 3.4vw, 3.2rem);
  color: var(--secondary);

  @media (max-width: 768px) {
    line-height: 1.2;
  }
`;

const Description = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.8;
  font-size: 1.05rem;
`;

const Carousel = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-lg);
  max-width: 1180px;
  margin: 0 auto;
  box-shadow: var(--shadow-card);

  @media (max-width: 768px) {
    border-radius: var(--radius-md);
  }
`;

const SlidesTrack = styled.div`
  display: flex;
  transition: transform 0.7s ease;
  transform: translateX(${({ $index }) => `-${$index * 100}%`});
`;

const Slide = styled.article`
  flex: 0 0 100%;
  position: relative;
  min-height: 420px;
  color: #ffffff;
  isolation: isolate;

  @media (max-width: 1024px) {
    min-height: 380px;
  }

  @media (max-width: 768px) {
    min-height: 340px;
  }

  @media (max-width: 540px) {
    min-height: 320px;
  }

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(190deg, rgba(12, 22, 40, 0.08) 0%, rgba(12, 22, 40, 0.7) 55%, rgba(12, 22, 40, 0.94) 100%);
    z-index: 1;
    transition: opacity 0.5s ease;
    opacity: ${({ $active }) => ($active ? 1 : 0.85)};
  }

  &:after {
    content: '';
    position: absolute;
    inset: -55% 0 auto;
    height: 60%;
    background: radial-gradient(circle at center, rgba(98, 164, 255, 0.5), transparent 70%);
    z-index: 0;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: translateY(${({ $active }) => ($active ? '-6px' : '10px')});
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
`;

const SlideImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  transform: scale(${({ $active }) => ($active ? 1.05 : 1)});
  transition: transform 0.8s ease;
`;

const SlideContent = styled.div`
  position: relative;
  z-index: 2;
  padding: clamp(2rem, 4vw, 3.4rem);
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 2.2rem 1.8rem;
    gap: 14px;
  }

  @media (max-width: 540px) {
    padding: 1.9rem 1.4rem;
  }
`;

const SlideTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  width: fit-content;
`;

const SlideTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.6rem, 2.4vw, 2.1rem);
  font-weight: 600;

  @media (max-width: 768px) {
    line-height: 1.35;
  }
`;

const SlideDescription = styled.p`
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.85);
`;

const Controls = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;

  @media (max-width: 768px) {
    inset: auto 0 14px;
    padding: 0 16px;
  }
`;

const ControlButton = styled.button`
  pointer-events: auto;
  border: none;
  background: rgba(12, 22, 40, 0.55);
  color: #fff;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  margin: 0 18px;

  &:hover {
    background: rgba(12, 77, 162, 0.8);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    margin: 0 10px;
  }
`;

const Indicators = styled.div`
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    bottom: 16px;
    gap: 10px;
  }
`;

const IndicatorDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.45)')};
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const featureImages = [featureOne, featureTwo, featureThree, featureFour];

const FeaturesSection = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const featureCards = t('home.features.cards');

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % featureCards.length);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? featureCards.length - 1 : prev - 1));
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % featureCards.length);
    }, 7000);

    return () => clearInterval(timer);
  }, [featureCards.length]);

  return (
    <FeaturesContainer>
        <SectionHeadingArea>
          <SectionTag>{t('home.features.tag')}</SectionTag>
          <SectionTitle>{t('home.features.title')}</SectionTitle>
          <Description>{t('home.features.description')}</Description>
        </SectionHeadingArea>
      <Carousel>
        <SlidesTrack $index={activeIndex}>
          {featureCards.map((card, index) => (
            <Slide key={card.title} $active={index === activeIndex}>
              <SlideImage src={featureImages[index]} alt={card.title} loading="lazy" $active={index === activeIndex} />
              <SlideContent>
                <SlideTag>{card.tag}</SlideTag>
                <SlideTitle>{card.title}</SlideTitle>
                <SlideDescription>{card.description}</SlideDescription>
              </SlideContent>
            </Slide>
          ))}
        </SlidesTrack>
        <Controls>
          <ControlButton type="button" onClick={handlePrev} aria-label={t('home.features.controls.prev')}>
            {'<'}
          </ControlButton>
          <ControlButton type="button" onClick={handleNext} aria-label={t('home.features.controls.next')}>
            {'>'}
          </ControlButton>
        </Controls>
        <Indicators>
          {featureCards.map((card, index) => (
            <IndicatorDot
              key={card.title}
              type="button"
              onClick={() => setActiveIndex(index)}
              $active={index === activeIndex}
              aria-label={`${t('home.features.controls.indicatorPrefix')} ${card.title}`}
            />
          ))}
        </Indicators>
      </Carousel>
    </FeaturesContainer>
  );
};

export default FeaturesSection;